import { Typography, Stack } from '@mui/material';

import { SxProps } from '../../../../types';
import { mergeSx } from '../../../../utils';
import { ToggleButton, ToggleButtonGroup } from '../../../buttons';
import { CastResetZoom, Crop } from '../../../icons';
import { Tooltip } from '../../../tooltip';
import { default as ZoomExample } from '../../_assets/ZoomExample.svg';
import { useChartContext } from '../../ChartProvider';
import { AnalyzeWithOpsPilotProps } from '../../types';
import { ChartAnalyzeButton } from './_components/ChartAnalyzeButton';
import { tokens } from '../../../../theme/generated/tokens';

const ChartZoomResetButton = () => {
  const { resetZoom } = useChartContext();

  return (
    <ToggleButton
      value="zoom"
      icon={
        <CastResetZoom color={tokens.theme.fg.structural.secondary} size={24} />
      }
      onClick={resetZoom}
      testId="reset-zoom-button"
    />
  );
};

const ChartZoomHintButton = () => {
  return (
    <Tooltip
      title={
        <Stack gap={8} maxWidth={238}>
          <Typography variant="P12R" sx={{ whiteSpace: 'pre-line' }}>
            {`This chart is zoomable.\n\nHold and drag your pointer on desired area of this chart and area will be zoomed.`}
          </Typography>
          <ZoomExample />
          <Typography variant="P12R">
            Also, you can zoom by clicking on clustered events counters.
          </Typography>
        </Stack>
      }
      placement="bottom-end"
      arrow={false}
      disableInteractive
      enterDelay={200}
      enterNextDelay={200}
    >
      <div>
        <ToggleButton
          value="zoom"
          icon={<Crop />}
          testId="zoomable-chart-button"
        />
      </div>
    </Tooltip>
  );
};

type ChartActionsProps = {
  hasNoData?: boolean;
  sx?: SxProps;
  analyzeWithOpsPilot?: AnalyzeWithOpsPilotProps;
};

export const ChartActions = ({
  hasNoData,
  sx,
  analyzeWithOpsPilot,
}: ChartActionsProps) => {
  const { hasZoomed, zoomEnabled, showZoomHintButton, zoomedRange } =
    useChartContext();

  const showActions = zoomEnabled;

  if (!showActions) {
    return null;
  }

  const { from, to } = zoomedRange;
  // Convert Unix timestamps (seconds) to milliseconds for JavaScript Date
  const zoomedRangeNumeric =
    typeof from === 'number' && typeof to === 'number'
      ? { from: from * 1000, to: to * 1000 }
      : undefined;

  return (
    <ToggleButtonGroup
      size="sm"
      sx={mergeSx(
        {
          position: 'absolute',
          top: 16,
          right: 16,
          zIndex: 1000,
        },
        sx
      )}
      testId="chart-actions"
      className="chart-actions"
    >
      {analyzeWithOpsPilot && !hasNoData && (
        <ChartAnalyzeButton
          getPrompt={analyzeWithOpsPilot.getPrompt}
          onClick={analyzeWithOpsPilot.onClick}
          disabled={analyzeWithOpsPilot.disabled}
          zoomedRange={zoomedRangeNumeric}
        />
      )}
      {zoomEnabled && hasZoomed && <ChartZoomResetButton />}
      {zoomEnabled && !hasZoomed && !hasNoData && showZoomHintButton && (
        <ChartZoomHintButton />
      )}
    </ToggleButtonGroup>
  );
};
